import React, { useState, useEffect, useRef } from "react";
import './PrepGuruPage.css';
import CompletedDetailsModal from './CompletedDetailsModal';
import MockLaunchPod from "./MockLaunchPod";
import JdDetailsModal from './JDDetailsModal';
import LoadTransitionPage from "./loadingpage/LoadTransitionPage";
import ResumePreview from './ResumePreview';
import { useAuthContext } from "../Auth/AuthContext";
import { Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import config from "../../config.json";
import TransitionPage from "./TransitionPage";
import BotGuruQueuePage from "./loadingpage/BotGuruQueuePage";


const MockGuruPage = () => {
    const { userObjId, profileData, userAnalytics,jdData,resumes,mocksessions,setMockSessions} = useAuthContext(); // Get userObjId and newUser from context
    const [isModalOpen, setModalOpen] = useState(false);
    const [selectedCard, setSelectedCard] = useState(null);
    const [launchPod, setLaunchPod] = useState(false);
    const [isJdModalOpen, setJdModalOpen] = useState(false);
    const [selectedJdId, setSelectedJdId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [sessionUrl, setSessionUrl] = useState(null);
    const [isIframeOpen, setIframeOpen] = useState(false);
    const [selectedResume, setSelectedResume] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const [reigniteSessionId, setReigniteSessionId] = useState(null);

    const navigate=useNavigate();
    const [showQueue,      setShowQueue]      = useState(false);   // spinner / error
    const [queueError,     setQueueError]     = useState(false);   // “no” path
    const [showTransition, setShowTransition] = useState(false);   // fallback
    const [urlArrived,     setUrlArrived]     = useState(false);   // flag from WS

    const firstRender            = useRef(true);
    const prevIsLoading          = useRef(false);
    const pendingUrl             = useRef(null);   // keeps URL until we’re ready
    const iframeOpenRequested    = useRef(false);  // “operationComplete” flag

    useEffect(() => {
        const done = (e) => {
            if (e.data === 'operationComplete') {
                iframeOpenRequested.current = true;

                if (pendingUrl.current) {
                    setUrlArrived(true);
                    setShowTransition(false);
                    setIsLoading(false);
                    setSessionUrl(pendingUrl.current);
                    setIframeOpen(true);
                    pendingUrl.current = null;
                }
            }
        };

        window.addEventListener('message', done);
        return () => window.removeEventListener('message', done);
    }, []);

    useEffect(() => {
        if (firstRender.current) { firstRender.current = false; return; }

        // show fallback only when:  ✅ spinner just ended  ✅ not in queue  ✅ url missing
        if (prevIsLoading.current && !isLoading && !showQueue && !urlArrived) {
            setShowTransition(true);
        }
        prevIsLoading.current = isLoading;
    }, [isLoading, showQueue, urlArrived]);


    const fetchMockSessions = async () => {
        try {
            const response = await fetch(`${config.restAPI}/sessions/getSessions/`, {
                method: "POST",
                credentials: 'include',
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    user_obj_id: userObjId,
                    session_type: "mockbot",
                }),
            });
            const data = await response.json();
            const sortedSessions = data.sessions.sort((a, b) => {
                const aDate = new Date(a.created_on);
                const bDate = new Date(b.created_on);
                return bDate - aDate; // Latest first
            });
            
            setMockSessions(sortedSessions || []);
            // setSessions(data.sessions || []);
        } catch (error) {
            console.error("Error fetching sessions:", error);
        }
    };
    
    useEffect(()=>{
        fetchMockSessions();
    },[])

    const openModal = (card) => {
        setSelectedCard(card);
        setModalOpen(true);
    };

    const openJdModal = (jdId) => {
        console.log("Opening JD modal with ID:", jdId);
        setSelectedJdId(jdId);
        setJdModalOpen(true);
    };

    const closeModal = () => setModalOpen(false);
    const closeLaunchModal = () => setLaunchPod(false);

    const totalSessions = userAnalytics?.total_mockbot || 1; // Avoid division by zero
    const usedSessions = userAnalytics?.used_mockbot || 0;
    const remainingSessions = userAnalytics?.remaining_mockbot || 0;

    const circumference = 2 * Math.PI * 54;
    const usedDash = (usedSessions / totalSessions) * circumference;
    const remainingDash = (remainingSessions / totalSessions) * circumference;

    const closeIframe = () => {
        setIframeOpen(false);
        setSessionUrl(null);
    };

    const handleViewResume = (resumeId) => {
        const resumeData = resumes.find(resume => resume._id === resumeId);

        if (!resumeData) {
            console.error('Resume not found');
            return;
        }
        const formattedResume = {
            message: {
                first_name: resumeData.resume.personalDetails.firstName,
                last_name: resumeData.resume.personalDetails.lastName,
                email: resumeData.resume.personalDetails.email,
                phone_number: resumeData.resume.personalDetails.phoneNumber,
                linkedin_url: resumeData.resume.personalDetails.linkedIn,
                website_url: resumeData.resume.personalDetails.website,
                professional_summary: resumeData.resume.personalDetails.summary,
                skills: resumeData.resume.skills,
                experience: resumeData.resume.experiences.map(exp => ({
                    job_role: exp.jobTitle,
                    company_name: exp.companyName,
                    start_date: exp.startDate,
                    end_date: exp.endDate,
                    responsibilities: exp.description ? exp.description.split('\n') : [],
                    city_state: exp.location,
                    country: exp.country,
                    skills: exp.skills || [],
                    domain: exp.domain
                })),
                education: resumeData.resume.education.map(edu => ({
                    school_university: edu.school,
                    degree: edu.degree,
                    start_date: edu.startDate,
                    end_date: edu.endDate,
                    gpa: edu.gpa,
                    city_state: edu.city_state,
                    country: edu.country,
                    additionalDetails: edu.additionalDetails
                }))
            }
        };
        setSelectedResume(formattedResume);
        setShowPreview(true);
    };

    const handleRedoClick = (session) => {
        const latestSession = session.sessions?.[0];
        if (!latestSession) { alert("No session data found."); return; }

        /* ── Stage‑0 : fresh run ─────────────────────────────── */
        setUrlArrived(false);
        setShowTransition(false);
        setShowQueue(true);        // immediate spinner
        setQueueError(false);
        setReigniteSessionId(session._id);

        iframeOpenRequested.current = false;  // reset
        pendingUrl.current = null;            // reset

        const websocket = new WebSocket("wss://ycbrz3lmnc.execute-api.us-west-2.amazonaws.com/production/");


        websocket.onopen = () => {
            const message = {action: "SendMessage", message: "get data"};
            websocket.send(JSON.stringify(message));
        };

        websocket.onmessage = async (event) => {
            if (!event.data || event.data.trim() === "") {
                console.warn("Received empty WebSocket message.");
                return;
            }

            const data = JSON.parse(event.data);
            if (data === "go") {
                setShowQueue(false);   // hide spinner
                setIsLoading(true);    // open loader
                return;
            }
            if (data === "no") {
                setQueueError(true);   // spinner -> error
                return;
            }

            if (data.connectionid) {

                const selectedData = {
                    user_id: userObjId,
                    user_obj_id: userObjId,
                    userfirstname: profileData?.name,
                    user_name: profileData?.name,
                    local_time: new Date().toLocaleString(),
                    user_email: session.email,
                    session_id: session._id,
                    resume_obj_id: session.resume_obj_id,
                    job_role: session.job_role,
                    job_description: session.job_description,
                    resumeId: session.resume_id,
                    persona: session.persona || "m",
                    interviewType: "technical",
                    connectionId: data.connectionid,
                    region: "us-west-1",
                    bot_type: "mockbot",
                };
            

                try {
                    const response = await fetch(`${config.restAPI}/sessions/reigniteSession/`, {
                        method: "POST",
                        credentials: 'include',
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(selectedData),
                    });

                    if (!response.ok) {
                        throw new Error(`Failed to send data. Status: ${response.status}`);
                    }

                } catch (error) {
                    console.error("Error sending data to API:", error.message);
                    setIsLoading(false);
                }
            } else if (data.url) {
                pendingUrl.current = data.url;

                // open iframe only after the loader’s iframe posts “operationComplete”
                if (iframeOpenRequested.current) {
                    setUrlArrived(true);
                    setShowTransition(false);
                    setIsLoading(false);
                    setSessionUrl(data.url);
                    setIframeOpen(true);
                    pendingUrl.current = null;
                }
            return
            } else {
                console.warn("No connection ID or URL received in WebSocket message.");
                setIsLoading(false);
            }
        };


        websocket.onerror = (error) => {
            console.error("WebSocket error:", error);
            setShowQueue(false);
            setIsLoading(false);
        };


        websocket.onclose = () => {
            // console.log("WebSocket connection closed.");
        };
    };


    const handleNewSessionClick = () => {
            setLaunchPod(true);
    };

    const handleSubscribeClick = (botType) => {
        const tabName = botType === 'mockbot' ? 'Mock Bot' : 'Prep Bot'; // Use the exact tab names
        navigate('/pricing', { state: { activeTabName: tabName } });
      };
 

    const renderNewPrepCard = () => {
        var hasSessions=userAnalytics?.total_mockbot>userAnalytics?.used_mockbot;
        return (
            <>
            {
                hasSessions ? (

                            <button 
                                className="create-session-button" 
                                onClick={() => handleNewSessionClick()}
                            >
                                <i className="fas fa-plus"></i>
                                Create New Session
                            </button>
                    
                ) : (
                    <button 
                    className="create-session-button" 
                    onClick={() => navigate("/pricing", { state: { activeTabName: 'Mock Bot' } }) }
                >
                    <i className="fas fa-lock"></i>
                    Create New Session
                </button>
                )
            }
            </>
        )
    };

    
    const renderCard = (session) => {
        if (!session.sessions[0].duration || session.sessions[0].duration == 0) {
            return null; 
        }    
        const latestSession = session.sessions[0] || {};
        const score = latestSession.score || 0;
        const circumference = 2 * Math.PI * 54;
        
        const achievedDash = (session.latest_score / 100) * circumference;
        const remainingDash = ((100 - session.latest_score) / 100) * circumference;

        const improvementScore = session.improvement_score || 0;
        const improvementClass = improvementScore < 0 ? 'improvement-negative' : 'improvement-positive';
        
        var reigniteSessions= session.reignite_session>=2|| userAnalytics?.remaining_mockbot<=0;

        return (
            <div 
                className="session-card" 
                key={session._id}
                onClick={() => openModal(session)}
            >
                 <div className="session-left">
                <div className="session-improvement">
                <div className="donut-wrapper">
                    <div className="donut-chart">
                        <svg width="110" height="105" viewBox="0 0 120 120">
                            {/* Base circle */}
                            <circle
                                cx="60"
                                cy="60"
                                r="54"
                                fill="none"
                                stroke="#2A2A3C"
                                strokeWidth="8"
                            />
                            {/* Remaining score */}
                            <circle
                                cx="60"
                                cy="60"
                                r="54"
                                fill="none"
                                stroke="rgba(190, 5, 250, 0.1)"
                                strokeWidth="8"
                                strokeDasharray={`${remainingDash} ${circumference}`}
                                strokeDashoffset={-achievedDash}
                                transform="rotate(-90 60 60)"
                                className="remaining-score"
                            />
                            {/* Achieved score */}
                            <circle
                                cx="60"
                                cy="60"
                                r="54"
                                fill="none"
                                stroke="var(--buttons-background-color)"
                                strokeWidth="8"
                                strokeDasharray={`${achievedDash} ${circumference}`}
                                strokeDashoffset="0"
                                transform="rotate(-90 60 60)"
                                className="achieved-score"
                            />
                            <text
                                x="60"
                                y="55"
                                textAnchor="middle"
                                dominantBaseline="middle"
                                className="score-text"
                                fill="var(--text-primary)"
                                fontSize="28"
                                fontWeight="bold"
                            >
                                {session.latest_score || "0"}%
                            </text>
                            <text
                                x="60"
                                y="75"
                                textAnchor="middle"
                                dominantBaseline="middle"
                                className="score-label"
                                fill="var(--text-primary)"
                                fontSize="12"
                            >
                                Latest Score
                            </text>
                        </svg>
                    </div>
                </div>
                <div>
                {latestSession.improvement && (
                    <div className={`improvement ${improvementClass}`}>
                        {improvementScore > 0 ? `+${improvementScore}` : improvementScore} Improvement
                    </div>
                )}
 
                {session.total_sessions >= 2&&(
                    <div className={`improvement-indicator ${improvementScore < 0 ? 'improvement-negative' : 'improvement-positive'}`}>
                        <span>{improvementScore > 0 ? `+${improvementScore}` : improvementScore}% Improvement</span>
                    </div>
                )}
                </div>
                </div>
                <div className="session-card-header">
                    {/* <div className="role-icon">
                        <i className="fas fa-briefcase"></i>
                    </div> */}
                    <div className="role-info">
                        <h3>{session.job_role}</h3>
                        <div className="session-card-details">
                            <button className="action-link" onClick={(e) => {
                                    e.stopPropagation(); // Prevent modal from opening
                                    openJdModal(session.job_obj_id);
                                }}>
                                 View Job Description
                                <i className="fas fa-external-link-alt link-icon"></i>
                            </button>
                            <button className="action-link" onClick={(e) => {
                                    e.stopPropagation(); 
                                    handleViewResume(session.resume_obj_id);
                                }}>
                                 View Resume
                                <i className="fas fa-external-link-alt link-icon"></i>
                            </button>
                            <span>{session.sessions.filter(session => session.duration !== undefined && session.duration !== null).length} Sessions Completed</span>
                        </div>
                    </div>
                </div>
                </div>
                {/* <div className="tooltip">Click here to open session details</div> */}
                <div className="session-right">
                <button 
                    className="redo-button" 
                    disabled={reigniteSessions}
                    onClick={(e) =>  {
                        e.stopPropagation();
                         handleRedoClick(session);
                        }}
                >
                     <i className="fas fa-redo   "></i>
                    Reignite
                </button>
                <div className="launch-view">
                    <i className="fas fa-chevron-right view-icon"  ></i>
                </div>
                </div>
            </div>
        );
    };

    return (
        <>
         {/*{isLoading && <LoadTransitionPage   url={`https://botguru.ai:5174?session_id=${reigniteSessionId || ''}`}  setIsLoading={setIsLoading} />}*/}
            {showQueue && (
                <BotGuruQueuePage
                    error={queueError}
                    onBack={() => { setShowQueue(false); setQueueError(false); }}
                />
            )}

            {isLoading && (
                <LoadTransitionPage
                    url={`https://botguru.ai:5174?session_id=${reigniteSessionId || ""}`}
                    setIsLoading={setIsLoading}
                />
            )}

            {!showQueue && !isLoading && showTransition && (
                <TransitionPage
                    setShowTransitionPage={setShowTransition}
                    cancel={urlArrived}
                />
            )}

            <div className="mock-guru-page" style={{position:"relative"}}>
                <div className="hero-section">
                    <div className="hero-content">
                        <div className="hero-left">
                            <h1 className="hero-title">
                                Your Personalized Interview Coach
                            </h1>
                            <div className="prep-hero-description">
                            <span className="prepBot-title">Upload your job description and resume to personalize your practice sessions!</span>
                            <span className="prepBot-subtitle">After each practice session, receive a detailed report with actionable insights. Ace your interview with confidence using MockBot!</span>
                            </div>
                        </div>
                    </div>
                </div>

                {userAnalytics?.total_mockbot > 0 &&(
                <div className="session-counter">
                     <svg width="100" height="100" viewBox="0 0 130 130">
                        {/* Base Circle (Background) */}
                        <circle
                            cx="60"
                            cy="60"
                            r="54"
                            fill="none"
                            stroke="#2A2A3C"
                            strokeWidth="8"
                        />

                        {/* Used Sessions (Progress) */}
                        <circle
                            cx="60"
                            cy="60"
                            r="54"
                            fill="none"
                            stroke="#BE05FA"
                            strokeWidth="8"
                            strokeDasharray={`${usedDash} ${circumference}`}
                            strokeDashoffset="0"
                            transform="rotate(-90 60 60)"
                            className="used-sessions"
                        />

                        {/* Remaining Sessions */}
                        <circle
                            cx="60"
                            cy="60"
                            r="54"
                            fill="none"
                            stroke="rgba(190, 5, 250, 0.3)"
                            strokeWidth="8"
                            strokeDasharray={`${remainingDash} ${circumference}`}
                            strokeDashoffset={-usedDash}
                            transform="rotate(-90 60 60)"
                            className="remaining-sessions"
                        />

                        {/* Session Count Text */}
                        <text
                            x="60"
                            y="55"
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fill="var(--text-primary)"
                            fontSize="18"
                            fontWeight="bold"
                        >
                            {usedSessions} / {totalSessions}
                        </text>

                        {/* Label Text */}
                        <text
                            x="60"
                            y="75"
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fill="var(--text-primary)"
                            fontSize="12"
                            fontWeight="bold"
                        >
                            Used Sessions
                        </text>
                    </svg>
                </div>
                )}


                {userAnalytics?.total_mockbot === 0 && (
                            <div className="no-session-box">
                                <div className="no-session-content">
                                    <Sparkles color='#BE05FA'/>
                                    <h3>Unlock Your Potential</h3>
                                    <p>Begin your journey with a practice interview  and receive insightful, personalized feedback.</p>
                                    <button className="no-session-button" onClick={() => handleSubscribeClick('mockbot')}>Subscribe for MockBot</button>
                                </div>
                            </div>
                       
                )}

                {userAnalytics?.total_mockbot > 0 && userAnalytics?.used_mockbot === 0 && (
                    <div className="no-session-box">
                        <div className="no-session-content">
                        <Sparkles color='#BE05FA'/>
                        <h3>No Active Sessions</h3>
                        <p>Start preparing by creating a new session.</p>
                        <button className="no-session-button" onClick={handleNewSessionClick}>
                            <i className="fas fa-plus"></i> Create New Session
                        </button>
                        </div>
                    </div>
                )}

                {userAnalytics?.used_mockbot > 0 && (
                    <>
                <div>
                {renderNewPrepCard()}
                </div>
               
                    <div className="card-container">
                    {[...mocksessions.map((session) => renderCard(session))]}
                    </div>
                    </>
                )}
                
                {/* </div> */}
            </div>
            <CompletedDetailsModal isOpen={isModalOpen} closeModal={closeModal} cardData={selectedCard} />
            {(launchPod &&
            <MockLaunchPod closeModal={closeLaunchModal} />
            )}
            <JdDetailsModal 
                isOpen={isJdModalOpen}
                closeModal={() => setJdModalOpen(false)}
                jdId={selectedJdId}
                jdData={jdData}
            />
            {showPreview && (
                <ResumePreview 
                    resumeData={selectedResume}
                    closeModal={() => {
                        setShowPreview(false);
                        setSelectedResume(null);
                    }}
                />
            )}
            
            {isIframeOpen && sessionUrl && (
                <div className="iframe-overlay">
                    <iframe
                        src={sessionUrl}
                        className="session-iframe"
                        allow="camera; microphone; fullscreen"
                        title="Session Iframe"
                    ></iframe>
                    <button className="iframe-close-button" onClick={closeIframe}>Close</button>
                </div>
            )}
        </>
    );
};

export default MockGuruPage;
